/**
 * รับออเดอร์ (Order Flow) — บอทถามทีละช่องแล้วสรุปยืนยันราคา (NO HARDCODE)
 * - ช่องข้อมูล: admin นิยามเอง + เลือก "แหล่ง" (ดึงช้อยส์จากเมนูจริง: สินค้า/สี/ไซซ์)
 * - คำถามต่อช่อง + ข้อความสรุป: แก้ได้เต็ม ใส่ตัวแปร {{...}} ได้
 * - เปิด/ปิด flow ทั้งระบบได้ (ปิด = บอทไม่รับออเดอร์อัตโนมัติ)
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  OrderFieldsApi, OrderFlowsApi, type OrderFieldItem, type OrderFlowItem,
} from '@/lib/admin-api';
import { useBrands, brandLabel } from '@/lib/useBrands';
import { toast } from 'sonner';

const SOURCES: { value: string; label: string; help: string }[] = [
  { value: 'product', label: '🛍️ สินค้า', help: 'ดึงช้อยส์จากเมนู "ราคา" (เสื้อ/กางเกง/ชุด) — ใช้คิดราคา' },
  { value: 'color', label: '🎨 สี', help: 'ดึงช้อยส์จากเมนู "สี"' },
  { value: 'size', label: '📏 ไซซ์', help: 'ดึงช้อยส์จากเมนู "ไซซ์"' },
  { value: 'number', label: '🔢 จำนวน', help: 'ตัวเลข เช่น 1, 2, 3 (นับเป็นชิ้น/ชุดตามสินค้า)' },
  { value: 'text', label: '📝 ข้อความอิสระ', help: 'ลูกค้าพิมพ์อะไรก็ได้ (เช่น ชื่อปัก)' },
];
const srcLabel = (s: string) => SOURCES.find((x) => x.value === s)?.label ?? s;

export default function OrderBuilderPage() {
  const qc = useQueryClient();
  const { keys } = useBrands();
  const [brand, setBrand] = useState<string>('scrub');
  const brands = keys.length ? keys : ['scrub', 'bow'];

  const { data: fields = [] } = useQuery({ queryKey: ['order-fields', brand], queryFn: () => OrderFieldsApi.list(brand) });
  const { data: flows = [] } = useQuery({ queryKey: ['order-flows', brand], queryFn: () => OrderFlowsApi.list(brand) });
  const flow = flows[0] as OrderFlowItem | undefined;

  const refreshF = () => qc.invalidateQueries({ queryKey: ['order-fields', brand] });
  const refreshFlow = () => qc.invalidateQueries({ queryKey: ['order-flows', brand] });

  const [nf, setNf] = useState({ field_key: '', label: '', source: 'text' });
  const addField = useMutation({
    mutationFn: () => OrderFieldsApi.create({ brand, field_key: nf.field_key, label: nf.label, source: nf.source, required: 1, sort_order: (fields.at(-1)?.sort_order ?? 0) + 1 }),
    onSuccess: () => { setNf({ field_key: '', label: '', source: 'text' }); refreshF(); toast.success('เพิ่มช่องแล้ว'); },
    onError: (e: any) => toast.error(e.message),
  });
  const saveField = (id: number, patch: Partial<OrderFieldItem>) => OrderFieldsApi.update(id, patch).then(refreshF).catch((e) => toast.error(e.message));

  // steps = [{field,q}] — เก็บคำถามต่อช่อง
  const steps: Record<string, string> = (() => {
    try { return Object.fromEntries((JSON.parse(flow?.steps ?? '[]') as { field: string; q: string }[]).map((s) => [s.field, s.q])); } catch { return {}; }
  })();
  const saveStep = (fieldKey: string, q: string) => {
    if (!flow) return;
    const next = fields.map((f) => ({ field: f.field_key, q: f.field_key === fieldKey ? q : (steps[f.field_key] ?? '') }));
    OrderFlowsApi.update(flow.id, { steps: JSON.stringify(next) }).then(refreshFlow).catch((e) => toast.error(e.message));
  };
  const saveFlow = (patch: Partial<OrderFlowItem>) => flow && OrderFlowsApi.update(flow.id, patch).then(refreshFlow).catch((e) => toast.error(e.message));

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
        <strong>🧾 รับออเดอร์อัตโนมัติ</strong> — บอทจะถามลูกค้าทีละช่อง (สินค้า → สี → ไซซ์ → จำนวน) แล้ว
        <strong> สรุปพร้อมราคาให้ยืนยัน</strong>. ช่องที่เป็น "สินค้า/สี/ไซซ์" จะดึงช้อยส์จากเมนูจริงให้อัตโนมัติ —
        เพิ่มสีใหม่ในเมนูสี บอทก็รู้จักทันที
      </div>

      {/* brand selector */}
      <div className="flex gap-1">
        {brands.map((b) => (
          <button key={b} onClick={() => setBrand(b)}
            className={`px-3 py-1 rounded text-sm ${brand === b ? 'bg-primary text-primary-foreground' : 'border border-border'}`}>
            {brandLabel(b)}
          </button>
        ))}
      </div>

      {/* flow on/off + summary */}
      <section className="border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">⚙️ สถานะการรับออเดอร์</h2>
          {flow ? (
            <button onClick={() => saveFlow({ active: flow.active ? 0 : 1 })}
              className={`px-3 py-1 rounded text-sm ${flow.active ? 'bg-green-600 text-white' : 'bg-muted'}`}>
              {flow.active ? '🟢 เปิดอยู่ (บอทรับออเดอร์)' : '⚪ ปิดอยู่ (บอทไม่รับออเดอร์)'}
            </button>
          ) : <span className="text-sm text-muted-foreground">ยังไม่มี flow สำหรับแบรนด์นี้</span>}
        </div>
        {flow && (
          <label className="text-sm space-y-1 block">
            <span className="font-medium">ข้อความสรุปยืนยัน</span>
            <textarea defaultValue={flow.summary_template ?? ''} rows={4}
              onBlur={(e) => e.target.value !== (flow.summary_template ?? '') && saveFlow({ summary_template: e.target.value })}
              className="w-full bg-white border border-input rounded px-2 py-1 outline-none focus:ring-1 focus:ring-ring font-mono text-xs" />
            <span className="text-xs text-muted-foreground">
              ตัวแปรที่ใช้ได้: <code>{'{{items}}'}</code> (รายการ+ราคา) • <code>{'{{total}}'}</code> (ยอดรวม) • <code>{'{{qty}}'}</code> •
              ชื่อช่องแต่ละช่อง เช่น <code>{'{{color}}'}</code> <code>{'{{size}}'}</code>
            </span>
          </label>
        )}
      </section>

      {/* add field */}
      <section className="border border-border rounded-lg p-4 space-y-3">
        <h2 className="font-semibold">+ เพิ่มช่องข้อมูลที่จะถามลูกค้า</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-sm space-y-1">
            <span className="font-medium">รหัสช่อง (key) <span className="text-red-500">*</span></span>
            <input value={nf.field_key} onChange={(e) => setNf({ ...nf, field_key: e.target.value })} placeholder="color"
              className="w-full bg-white border border-input rounded px-2 py-1 outline-none focus:ring-1 focus:ring-ring" />
            <span className="text-xs text-muted-foreground">ใช้เป็นตัวแปร {'{{key}}'} ในข้อความสรุป</span>
          </label>
          <label className="text-sm space-y-1">
            <span className="font-medium">ป้ายชื่อ <span className="text-red-500">*</span></span>
            <input value={nf.label} onChange={(e) => setNf({ ...nf, label: e.target.value })} placeholder="สี"
              className="w-full bg-white border border-input rounded px-2 py-1 outline-none focus:ring-1 focus:ring-ring" />
          </label>
          <label className="text-sm space-y-1">
            <span className="font-medium">แหล่งช้อยส์</span>
            <select value={nf.source} onChange={(e) => setNf({ ...nf, source: e.target.value })}
              className="w-full bg-white border border-input rounded px-2 py-1 outline-none focus:ring-1 focus:ring-ring">
              {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <span className="text-xs text-muted-foreground">{SOURCES.find((s) => s.value === nf.source)?.help}</span>
          </label>
        </div>
        <button disabled={!nf.field_key.trim() || !nf.label.trim() || addField.isPending}
          onClick={() => addField.mutate()}
          className="px-4 py-1.5 rounded bg-primary text-primary-foreground text-sm disabled:opacity-50">เพิ่มช่อง</button>
      </section>

      {/* fields list */}
      <section className="space-y-2">
        <h2 className="font-semibold">ช่องข้อมูล + คำถาม ({fields.length}) — ลำดับการถาม</h2>
        <table className="w-full text-sm border border-border rounded">
          <thead className="bg-muted/40 text-xs">
            <tr>
              <th className="px-2 py-1 w-8">#</th>
              <th className="px-2 py-1 text-left w-28">ป้ายชื่อ</th>
              <th className="px-2 py-1 text-left w-32">แหล่งช้อยส์</th>
              <th className="px-2 py-1 text-left">คำถามที่บอทถาม</th>
              <th className="px-2 py-1 w-14">เปิด</th>
              <th className="px-2 py-1 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {fields.map((f, i) => (
              <tr key={f.id}>
                <td className="px-1 py-1 text-center"><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">{i + 1}</span></td>
                <td className="px-1 py-1">
                  <input defaultValue={f.label} className="w-full bg-white border border-input rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-ring"
                    onBlur={(e) => e.target.value !== f.label && saveField(f.id, { label: e.target.value })} />
                  <code className="text-[10px] text-muted-foreground">{f.field_key}</code>
                </td>
                <td className="px-1 py-1">
                  <select defaultValue={f.source} onChange={(e) => saveField(f.id, { source: e.target.value })}
                    className="bg-white border border-input rounded px-1 py-0.5 text-xs">
                    {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </td>
                <td className="px-1 py-1">
                  <input defaultValue={steps[f.field_key] ?? ''} placeholder={`เช่น รับ${f.label}ไหนดีคะ`}
                    disabled={!flow}
                    className="w-full bg-white border border-input rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-ring text-xs disabled:bg-muted"
                    onBlur={(e) => e.target.value !== (steps[f.field_key] ?? '') && saveStep(f.field_key, e.target.value)} />
                </td>
                <td className="px-1 py-1 text-center"><button onClick={() => saveField(f.id, { active: f.active ? 0 : 1 })}>{f.active ? '🟢' : '⚪'}</button></td>
                <td className="px-1 py-1 text-center"><button className="text-red-600" onClick={() => confirm(`ลบช่อง "${f.label}"?`) && OrderFieldsApi.remove(f.id).then(refreshF)}>ลบ</button></td>
              </tr>
            ))}
            {!fields.length && <tr><td colSpan={6} className="text-center text-muted-foreground py-4">ยังไม่มีช่องข้อมูล — เพิ่มด้านบน</td></tr>}
          </tbody>
        </table>
        <p className="text-xs text-muted-foreground">หมายเหตุ: ช่อง "แหล่งช้อยส์ = {srcLabel('product')}" ใช้คิดราคา • จำนวนนับเป็นชิ้น/ชุดตามสินค้า ไม่มี "ครึ่งชุด"</p>
      </section>
    </div>
  );
}
